/** @typedef {'chat' | 'manage'} Vertex360CopilotWorkspaceView */

/** @typedef {Object} Vertex360CopilotConversation
 * @property {string} id
 * @property {string} title
 * @property {boolean} starred
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {import('./vertex360CopilotConstants').Vertex360CopilotMessage[]} messages
 */

export const VERTEX360_COPILOT_WORKSPACE_VIEWS = {
  CHAT: "chat",
  MANAGE: "manage",
};

export const VERTEX360_COPILOT_SIDEBAR_COLLAPSED_KEY = "vertex360.copilot.sidebarCollapsed";

export const VERTEX360_COPILOT_SIDEBAR_WIDTH_EXPANDED = 280;
export const VERTEX360_COPILOT_SIDEBAR_WIDTH_COLLAPSED = 56;

/**
 * Conversations fetched per request for Starred / Recent sidebar sections
 * (and infinite-scroll pages).
 */
export const VERTEX360_COPILOT_CONVERSATIONS_PAGE_SIZE = 50;

/** @deprecated Use VERTEX360_COPILOT_CONVERSATIONS_PAGE_SIZE */
export const VERTEX360_COPILOT_SIDEBAR_SECTION_MAX_ITEMS =
  VERTEX360_COPILOT_CONVERSATIONS_PAGE_SIZE;

/** Messages fetched per page when loading chat history (initial + older). */
export const VERTEX360_COPILOT_MESSAGES_PAGE_SIZE = 10;

/** Shared sidebar nav / chat-row active surface (see vertex360Copilot.css). */
export const VERTEX360_COPILOT_SIDEBAR_ITEM_CLASS = "copilot-sidebar-item";
export const VERTEX360_COPILOT_SIDEBAR_ITEM_ACTIVE_CLASS = "copilot-sidebar-item--active";

/**
 * @returns {string}
 */
export const createConversationId = () =>
  `conv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * @param {string} text
 * @returns {string}
 */
export const deriveConversationTitle = (text) => {
  const trimmed = String(text || "").trim();
  if (!trimmed) return "New chat";
  if (trimmed.length <= 48) return trimmed;
  return `${trimmed.slice(0, 45)}…`;
};

/** Identity tokens must never appear in chat titles (header / sidebar). */
const IDENTITY_TOKEN_RE =
  /\[(?:PARTICIPANT|WORKER|MANAGER|STAFF|EXTERNAL_SP)_(?:NAME|EMAIL|NDISNO|PHONE):[0-9a-fA-F-]{36}\]/i;
const PERSON_N_TOKEN_RE = /person_\d+(?:\.[A-Za-z]+)?/i;

export const conversationTitleHasIdentityLeak = (title) => {
  const raw = String(title || "");
  return IDENTITY_TOKEN_RE.test(raw) || PERSON_N_TOKEN_RE.test(raw);
};

const stripIdentityTokens = (text) =>
  String(text || "")
    .replace(
      /\[(?:PARTICIPANT|WORKER|MANAGER|STAFF|EXTERNAL_SP)_(?:NAME|EMAIL|NDISNO|PHONE):[0-9a-fA-F-]{36}\]/gi,
      " "
    )
    .replace(/person_\d+(?:\.[A-Za-z]+)?/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Title shown in the chat header and sidebar.
 * Prefers the first user message when the stored title leaked a PII token.
 *
 * @param {string} title
 * @param {string} [fallbackText]
 * @returns {string}
 */
export const displayConversationTitle = (title, fallbackText) => {
  const raw = String(title || "").trim();
  if (conversationTitleHasIdentityLeak(raw)) {
    const fallback = String(fallbackText || "").trim();
    if (fallback && !conversationTitleHasIdentityLeak(fallback)) {
      return deriveConversationTitle(fallback);
    }
    return deriveConversationTitle(stripIdentityTokens(raw)) || "New chat";
  }
  return raw || "New chat";
};

/**
 * @param {Vertex360CopilotConversation[]} conversations
 * @returns {Vertex360CopilotConversation[]}
 */
export const sortConversationsByActivity = (conversations) =>
  [...(conversations || [])].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
